/**
 * @fileoverview CopyableValue — hover-to-copy wrapper for displayed outputs.
 * @module components/jobs/phases/overview/CopyableValue
 *
 * Purpose: Show a copy icon on hover next to (or over) a displayed value so
 * practitioners can copy entity, appointment, and background details without
 * selecting text. On devices without hover the icon stays visible.
 */

import {
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import { Check, Copy } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/utils/misc";

interface CopyableValueProps {
  /** Plain text written to the clipboard. Empty / missing hides the button. */
  value?: string | null;
  /** Accessible name, e.g. "Company Name". */
  label: string;
  children: ReactNode;
  className?: string;
  /** `inline` sits the icon beside the value; `overlay` pins it to the corner. */
  placement?: "inline" | "overlay";
}

/**
 * Wraps displayed output and reveals a copy control on hover (or always, on
 * touch). Copies `value`, not the rendered children — so labels and helper
 * text next to a name are not included.
 *
 * @param props.value - Plain text written to the clipboard.
 * @param props.label - Accessible name for the copy control.
 * @param props.children - Displayed output the control sits beside or over.
 * @param props.className - Optional wrapper class.
 * @param props.placement - `inline` beside the value; `overlay` in the corner.
 * @returns The displayed value with a copy control when `value` is non-empty.
 *
 * @example
 * <CopyableValue value={job.entityName} label="Company Name">
 *   <span className="text-sm">{job.entityName}</span>
 * </CopyableValue>
 */
export function CopyableValue({
  value,
  label,
  children,
  className,
  placement = "inline",
}: CopyableValueProps) {
  const [isCopied, setIsCopied] = useState(false);
  const copiedResetRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copyText = value?.trim() ?? "";
  const canCopy = copyText.length > 0;

  useEffect(() => {
    return () => {
      if (copiedResetRef.current !== null) {
        clearTimeout(copiedResetRef.current);
      }
    };
  }, []);

  const handleCopy = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(copyText);
      if (copiedResetRef.current !== null) {
        clearTimeout(copiedResetRef.current);
      }
      setIsCopied(true);
      copiedResetRef.current = setTimeout(() => {
        setIsCopied(false);
        copiedResetRef.current = null;
      }, 1500);
    } catch {
      toast.error("Couldn't copy to clipboard");
    }
  };

  const button = canCopy ? (
    <button
      type="button"
      title={isCopied ? "Copied" : `Copy ${label}`}
      aria-label={isCopied ? "Copied" : `Copy ${label}`}
      onClick={(event) => void handleCopy(event)}
      className={cn(
        "inline-flex size-5 shrink-0 items-center justify-center rounded-sm text-muted-foreground transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        placement === "inline" && "mt-0.5",
        isCopied
          ? "opacity-100"
          : "opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover/copy:opacity-100",
      )}
    >
      {isCopied ? (
        <Check className="size-3 text-emerald-600" />
      ) : (
        <Copy className="size-3" />
      )}
    </button>
  ) : null;

  if (placement === "overlay") {
    return (
      <div className={cn("group/copy relative", className)}>
        {button ? (
          <div className="absolute right-4 top-3 z-10 rounded-sm bg-background/90">
            {button}
          </div>
        ) : null}
        {children}
      </div>
    );
  }

  return (
    <div className={cn("group/copy flex min-w-0 items-start gap-1", className)}>
      {/*
        Size to the displayed value so the copy control sits immediately
        after the text (same as director names). `flex-1` stretched this
        wrapper across the grid cell and parked the icon at the far edge.
      */}
      <div className="w-fit min-w-0 max-w-full">{children}</div>
      {button}
    </div>
  );
}
